import { defineStore } from 'pinia';
import type { LocationQuery, Router } from 'vue-router';
import { http } from '@/http';
import { useLoginStore } from './login';
import { unref, type Ref } from 'vue';
import { useLocalStorage, usePreferredDark } from '@vueuse/core';

export type ColorScheme = 'system' | 'light' | 'dark';

// migrate the legacy boolean darkTheme preference
function defaultColorScheme(): ColorScheme {
  try {
    const legacy = localStorage.getItem('darkTheme');
    localStorage.removeItem('darkTheme');
    if (legacy === null) {
      return 'system';
    }
    return legacy === 'true' ? 'dark' : 'light';
  } catch {
    return 'system';
  }
}

export const useAppStore = defineStore('app', {
  state: () => {
    const colorScheme = useLocalStorage<ColorScheme>('colorScheme', defaultColorScheme);
    const preferredDark = usePreferredDark();
    return {
      colorScheme,
      preferredDark,
      loading: 0,
      maintenance: false,
      router: null as Ref<Router> | null,
      isInIframe: window.location !== window.parent.location,
    };
  },

  getters: {
    darkTheme(state): boolean {
      return state.colorScheme === 'dark' || (state.colorScheme === 'system' && state.preferredDark);
    },
  },

  actions: {
    async logout(withRef = false) {
      await http.post('/api/auth/logout');

      const login = useLoginStore();
      login.$reset();

      const route = unref(this.router?.currentRoute);

      if (route?.meta.requiresAuth) {
        const query: LocationQuery = {};
        if (withRef && route.fullPath !== '/') {
          query.res = route.fullPath;
        }
        this.router?.push({ name: 'login', query });
      }
    },

    route() {
      return unref(this.router?.currentRoute);
    },
  },
});
